import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import ChevronDownSvg from "@redotech/redo-icons/chevron-down.svg";
import HelpCircleSvg from "@redotech/redo-icons/help-circle.svg";
import LayoutLeftSvg from "@redotech/redo-icons/layout-left.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useCallback } from "react";
import { useInfiniteScroll } from "../hooks/use-infinite-scroll";
import {
  ConversationInfoCard,
  ConversationInfoCardEmpty,
  ConversationInfoCardSkeleton,
} from "./conversation-info-card";

export interface ConversationCardListProps {
  conversations: ConversationListItemResponse[];
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  onCardClick: (conversation: ConversationListItemResponse) => void;
  refresh: () => void;
  onLoadMore: () => void;
  isCardSelected: (conversation: ConversationListItemResponse) => boolean;
  onCollapse: () => void;
}

export const ConversationCardList = memo(function ConversationCardList({
  conversations,
  isLoading,
  isFetchingMore,
  hasMore,
  onCardClick,
  refresh,
  onLoadMore,
  isCardSelected,
  onCollapse,
}: ConversationCardListProps) {
  const { scrollAreaRef, sentinelRef } = useInfiniteScroll({
    hasMore,
    isFetchingMore,
    onLoadMore,
  });

  const selectedCardRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      node.scrollIntoView({ block: "center" });
    }
  }, []);

  return (
    <Flex bgColor="primary" dir="column" height="full">
      {/* Header */}
      <Flex
        align="center"
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        gap="sm"
        pl="xl"
        pr="md"
        shrink={0}
        style={{ height: 48 }}
      >
        <Flex align="center" cursor="pointer" gap="xs">
          <Text fontSize="sm" fontWeight="semibold" textColor="secondary">
            All tickets
          </Text>
          <Icon arbiterIconSvg={ChevronDownSvg} color="ghost" size="compact" />
        </Flex>
        <Flex align="center" flex={1} gap="xs" justify="flex-end">
          <Tooltip arrow title="Navigate ticket list  ↑ / ↓">
            <Flex align="center">
              <Icon
                arbiterIconSvg={HelpCircleSvg}
                color="ghost"
                size="extra-small"
              />
            </Flex>
          </Tooltip>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={LayoutLeftSvg}
            onClick={onCollapse}
            size="sm"
          />
        </Flex>
      </Flex>

      {/* Card list */}
      <Flex
        dir="column"
        flex={1}
        overflowY="auto"
        ref={scrollAreaRef}
        style={{ minHeight: 0 }}
      >
        {isLoading ? (
          <ConversationInfoCardSkeleton count={5} />
        ) : conversations.length === 0 ? (
          <ConversationInfoCardEmpty />
        ) : (
          <>
            {conversations.map((conversation) => {
              const selected = isCardSelected(conversation);
              return (
                <div
                  key={conversation.id}
                  ref={selected ? selectedCardRef : undefined}
                >
                  <ConversationInfoCard
                    conversation={conversation}
                    isActive={selected}
                    onClick={() => onCardClick(conversation)}
                  />
                </div>
              );
            })}
            {/* Sentinel for infinite scroll */}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {isFetchingMore && <ConversationInfoCardSkeleton count={2} />}
          </>
        )}
      </Flex>
    </Flex>
  );
});
